import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Minus, Plus, ShoppingBag, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api.get(`/products/${id}`).then((res) => {
      setProduct(res.data);
      setSize(res.data.sizes?.[0] || "");
    }).catch(() => navigate("/shop"));
  }, [id, navigate]);

  if (!product) {
    return <div className="min-h-[60vh] flex items-center justify-center"><p className="font-display text-3xl text-[#4A4A4A]">Loading…</p></div>;
  }

  const add = () => {
    if (!size) return toast.error("Please select a size");
    addItem(product, size, qty);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-16" data-testid="product-detail-page">
      <Link to="/shop" className="inline-flex items-center gap-2 font-display text-xl uppercase mb-8 hover:text-[#D35400]" data-testid="back-to-shop">
        <ArrowLeft className="w-5 h-5" /> Back to Shop
      </Link>
      <div className="grid md:grid-cols-2 gap-12">
        <div className="border-2 border-[#1C1C1C] hard-shadow bg-[#F5F2EA] p-3">
          <img src={product.image} alt={product.name} className="w-full aspect-square object-cover" data-testid="product-image" />
        </div>
        <div>
          <span className="text-xs uppercase tracking-[0.25em] font-bold text-[#D35400]">{product.collection}</span>
          <h1 className="font-display text-5xl md:text-6xl mt-2 leading-none" data-testid="product-name">{product.name}</h1>
          <div className="text-3xl font-bold text-[#C0392B] mt-4" data-testid="product-price">${product.price.toFixed(2)}</div>
          <p className="mt-6 text-lg text-[#4A4A4A] leading-relaxed" data-testid="product-description">{product.description}</p>

          <div className="mt-8">
            <h3 className="font-display text-2xl mb-3">Size</h3>
            <div className="flex flex-wrap gap-2">
              {product.sizes.map((s) => (
                <button
                  key={s}
                  onClick={() => setSize(s)}
                  data-testid={`size-${s}`}
                  className={`min-w-[48px] px-4 py-2 border-2 border-[#1C1C1C] font-bold transition-all ${
                    size === s ? "bg-[#1C1C1C] text-[#FDFBF7]" : "hover:bg-[#EDE8DD]"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8">
            <h3 className="font-display text-2xl mb-3">Quantity</h3>
            <div className="flex items-center border-2 border-[#1C1C1C] w-fit">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 py-3 hover:bg-[#EDE8DD]" data-testid="qty-minus"><Minus className="w-4 h-4" /></button>
              <span className="px-6 font-bold text-lg" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="px-4 py-3 hover:bg-[#EDE8DD]" data-testid="qty-plus"><Plus className="w-4 h-4" /></button>
            </div>
          </div>

          <button onClick={add} className="btn-primary px-8 py-4 mt-9 inline-flex items-center gap-2 w-full sm:w-auto justify-center" data-testid="add-to-cart">
            <ShoppingBag className="w-5 h-5" /> Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
